import { useContext } from 'react';
import { Table, Space, Tooltip, Button, Flex, Popconfirm, Pagination, Select, message } from 'antd';
import { BankOutlined, EyeInvisibleOutlined, DownloadOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { IGlobalFile } from '@leedpulse-monorepo/interfaces';
import { TFileOwnerType } from '@leedpulse-monorepo/types';
import { USERS_PAGE_PATH } from '../../pages/users/UsersPage';
import { COMPANIES_PAGE_PATH } from '../../pages/companies/CompaniesPage';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import downloadUserFile from '../../../domain/use_cases/users/downloadUserFile';
import deleteUserFiles from '../../../domain/use_cases/users/deleteUserFiles';
import downloadCompanyFile from '../../../domain/use_cases/companies/downloadCompanyFile';
import deleteCompanyFiles from '../../../domain/use_cases/companies/deleteCompanyFiles';

interface FilesListProps {
    items: IGlobalFile[];
    loading: boolean;
    page: number;
    total: number;
    hitsPerPage: number;
    ownerType: TFileOwnerType | 'all';
    onOwnerTypeChange: (ownerType: TFileOwnerType | 'all') => void;
    onPaginationChange: (page: number, pageSize: number) => void;
    onRefresh: () => void;
}

// Une fiche entreprise et une fiche utilisateur n'ont ni la meme route ni le meme
// use case : le type de proprietaire decide des deux.
const isCompanyFile = (record: IGlobalFile) => record.ownerType === 'company';

const ownerPath = (record: IGlobalFile) =>
    `${isCompanyFile(record) ? COMPANIES_PAGE_PATH : USERS_PAGE_PATH}/${record.ownerId}?tab=files&file=${record.id}`;

export default function FilesList({ items, loading, page, total, hitsPerPage, ownerType, onOwnerTypeChange, onPaginationChange, onRefresh }: FilesListProps) {
    const authContext = useContext(AuthenticationContext);

    const handleDownload = async (record: IGlobalFile) => {
        try {
            const { url, filename } = isCompanyFile(record)
                ? await downloadCompanyFile(record.ownerId, record, authContext.authentication)
                : await downloadUserFile(record.ownerId, record, authContext.authentication);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors du téléchargement');
        }
    };

    const handleDeleteFile = async (record: IGlobalFile) => {
        try {
            if (isCompanyFile(record)) {
                await deleteCompanyFiles(record.ownerId, [record.id], authContext.authentication);
            } else {
                await deleteUserFiles(record.ownerId, [record.id], authContext.authentication);
            }
            message.success('Fichier supprimé avec succès');
            onRefresh();
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors de la suppression');
        }
    };

    const columns = [
        {
            title: '',
            key: 'ownerType',
            width: 48,
            render: (record: IGlobalFile) => (
                <Tooltip title={isCompanyFile(record) ? 'Entreprise' : 'Utilisateur'}>
                    {isCompanyFile(record) ? <BankOutlined /> : <UserOutlined />}
                </Tooltip>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (timestamp: number) => moment(timestamp).format('DD/MM/YYYY à HH:mm'),
        },
        {
            title: 'Entité',
            key: 'owner',
            render: (record: IGlobalFile) => (
                <Link to={ownerPath(record)} target="_blank" rel="noopener noreferrer">
                    {record.ownerName}
                </Link>
            ),
        },
        {
            title: 'Nom',
            dataIndex: 'name',
            key: 'name',
            render: (_: unknown, record: IGlobalFile) => (
                <Space size="small">
                    {record.sensitive && (
                        <Tooltip title="Fichier sensible">
                            <EyeInvisibleOutlined style={{ color: '#cf1322' }} />
                        </Tooltip>
                    )}
                    {record.name}
                </Space>
            ),
        },
        {
            title: '',
            key: 'actions',
            render: (record: IGlobalFile) =>
                <Flex gap="small" justify="end">
                    <Button type="primary" onClick={() => handleDownload(record)} icon={<DownloadOutlined />} />

                    <Popconfirm
                        title="Supprimer le fichier"
                        description="Cette action est irréversible. Confirmer la suppression ?"
                        onConfirm={() => handleDeleteFile(record)}
                        okText="Supprimer"
                        cancelText="Annuler"
                    >
                        <Button danger type="primary" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Flex>
        },
    ];

    return (
        <>
            <Flex align="center" justify="space-between" style={{ marginBottom: '1rem' }}>
                <Select
                    value={ownerType}
                    onChange={onOwnerTypeChange}
                    options={[
                        { label: 'Tous', value: 'all' },
                        { label: 'Utilisateurs', value: 'user' },
                        { label: 'Entreprises', value: 'company' },
                    ]}
                    style={{ width: '11.25rem' }}
                    disabled={loading}
                />
                <Pagination
                    current={page}
                    total={total}
                    pageSize={hitsPerPage}
                    onChange={onPaginationChange}
                    showSizeChanger={true}
                    pageSizeOptions={[20, 50, 100, 200]}
                    disabled={loading}
                />
            </Flex>

            <Table
                pagination={{
                    current: page,
                    total: total,
                    pageSize: hitsPerPage,
                    onChange: onPaginationChange,
                    showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100, 200],
                    position: ['bottomRight'],
                    disabled: loading,
                }}
                loading={loading}
                columns={columns}
                rowKey={(record) => record.id}
                dataSource={items}
            />
        </>
    );
}
